import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "fabj_cart_v1";

const keyOf = (i) => `${i.artwork_id}|${i.size}|${i.frame_color || ""}|${i.material || "poster"}`;

// Business collections: best volume tier for this many sets (per-location discount).
export const tierFor = (item) => (item.volume_tiers || []).filter(t => item.quantity >= t.min_qty).sort((a, b) => b.percent_off - a.percent_off)[0] || null;
export const effectiveUnit = (item) => {
  const t = tierFor(item);
  return t ? Math.round(item.unit_price * (1 - t.percent_off / 100) * 100) / 100 : item.unit_price;
};
export const lineTotal = (item) => effectiveUnit(item) * item.quantity;

const artworkToItem = (art, sizeOption, frame, materialOption) => {
  // frame: null | { frame_color, upsell_usd }; materialOption: null | { material, label, retail_price_usd }
  const material = materialOption?.material || "poster";
  return {
    artwork_id: art.id,
    title: art.title,
    image: art.image_urls?.[0] || "",
    size: sizeOption.size,
    unit_price: materialOption
      ? Number(materialOption.retail_price_usd)
      : Number(sizeOption.price) + (frame?.upsell_usd ? Number(frame.upsell_usd) : 0),
    base_price: materialOption ? Number(materialOption.retail_price_usd) : Number(sizeOption.price),
    frame_color: frame?.frame_color || null, // poster add-on frame, or the frame colour of a framed material
    frame_upsell: material === "poster" ? (frame?.upsell_usd || 0) : 0,
    material,
    material_label: materialOption?.label || "Museum Paper",
    quantity: 1,
    prints_only: !!art.prints_only,
  };
};

// B2B collection: one flat-priced line; the server expands it into its prints at checkout.
const collectionToItem = (col) => ({
  artwork_id: col.id, collection_id: col.id, title: `${col.name} — Business Collection (${col.artworks?.length || 0} prints)`,
  image: col.cover_image_url || col.artworks?.[0]?.image_urls?.[0] || "", size: col.print_size || "16x20",
  unit_price: Number(col.price_usd), base_price: Number(col.price_usd), frame_color: null, frame_upsell: 0,
  material: col.material || "poster", material_label: "Business Set", quantity: 1, prints_only: true,
  volume_tiers: col.volume_tiers || [],
});

const useCartPersistence = () => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);
  return [items, setItems];
};

const useCartOperations = (setItems) => {
  const pushItem = useCallback((newItem) => {
    const nk = keyOf(newItem);
    setItems((prev) => {
      const idx = prev.findIndex((p) => keyOf(p) === nk);
      if (idx >= 0) {
        if (!newItem.prints_only) return prev; // originals are one-of-a-kind
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, newItem];
    });
  }, [setItems]);

  const add = useCallback((art, sizeOption, frame = null, materialOption = null) => pushItem(artworkToItem(art, sizeOption, frame, materialOption)), [pushItem]);
  const addCollection = useCallback((col) => pushItem(collectionToItem(col)), [pushItem]);

  const remove = useCallback((artwork_id, size, frame_color = null, material = "poster") => {
    const nk = keyOf({ artwork_id, size, frame_color, material });
    setItems((p) => p.filter((i) => keyOf(i) !== nk));
  }, [setItems]);

  const setQty = useCallback((artwork_id, size, qty, frame_color = null, material = "poster") => {
    const nk = keyOf({ artwork_id, size, frame_color, material });
    setItems((p) => p.map((i) => (keyOf(i) === nk ? { ...i, quantity: i.prints_only ? Math.max(1, qty) : 1 } : i)));
  }, [setItems]);

  const clear = useCallback(() => setItems([]), [setItems]);
  return { add, addCollection, remove, setQty, clear };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useCartPersistence();
  const ops = useCartOperations(setItems);
  const value = useMemo(() => ({
    items,
    ...ops,
    total: items.reduce((s, i) => s + lineTotal(i), 0),
    count: items.reduce((s, i) => s + i.quantity, 0),
    effectiveUnit,
    lineTotal,
  }), [items, ops]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
